// FORMAT FOR CALCULATIONS

import config from '@/config';
import {
  AssetType,
  LPOrderType,
  OrderHistoryFilterType,
  OrderStatus,
  OrderType,
  TableTabType,
  TransferType,
} from '@/types/enums';

export interface BigDecimal {
  bigint: bigint;
  decimal: string;
}

export function parseScientificNotationToDecimalStr(value: string): string {
  const trimmed = value.trim();

  if (!/e/i.test(trimmed)) {
    return trimmed;
  }

  const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(trimmed);

  if (!match) {
    return trimmed;
  }

  const sign = match[1] ?? '';
  const intPart = match[2] ?? '0';
  const fracPart = match[3] ?? '';
  const expRaw = match[4];

  if (!expRaw) {
    return `${sign}${intPart}${fracPart ? `.${fracPart}` : ''}`;
  }

  const exponent = Number.parseInt(expRaw, 10);

  if (!Number.isFinite(exponent)) {
    return trimmed;
  }

  const digits = `${intPart}${fracPart}`;

  if (digits.length === 0) {
    return '0';
  }

  const decimalIndex = intPart.length + exponent;

  if (decimalIndex >= digits.length) {
    return `${sign}${digits.padEnd(decimalIndex, '0')}`;
  }

  if (decimalIndex > 0) {
    return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
  }

  return `${sign}0.${'0'.repeat(-decimalIndex)}${digits}`;
}

export function parseDecimalToBigInt(
  decimalStr: string | undefined | null,
  decimals: number | bigint,
): bigint {
  try {
    // Return 0n if decimalStr is undefined or empty
    if (!decimalStr) {
      return 0n;
    }

    const normalizedDecimalStr =
      parseScientificNotationToDecimalStr(decimalStr);

    const parts = normalizedDecimalStr.split('.');
    const whole = parts[0];
    let fraction = parts[1] ?? '';

    if (fraction.length > Number(decimals)) {
      // truncate
      // console.debug(
      //   `DecimalStr ${decimalStr} is longer than decimals ${decimals}, truncating`,
      // );
      fraction = fraction.slice(0, Number(decimals));
    } else if (fraction.length < Number(decimals)) {
      fraction = fraction.padEnd(Number(decimals), '0');
    }
    return BigInt(whole + fraction);
  } catch (e) {
    console.error(`Error parsing "${decimalStr}" to BigInt:`, e);
    return 0n;
  }
}

export function parseInputToDecimalStr(
  value: string,
  decimals: number | bigint,
): string {
  const parsed = parseDecimalToBigInt(value, decimals);
  return bigIntToDecimalStr(parsed, decimals);
}

export function bigIntToDecimalStr(value: bigint, decimals: number | bigint) {
  if (Number(decimals) === 0) {
    return value.toString();
  }

  const absVal = value >= 0n ? value : -value;
  const sign = value >= 0n ? '' : '-';
  const valStr = absVal.toString();
  const decimalPointIdx = valStr.length - Number(decimals);
  let decimalStr: string;
  if (decimalPointIdx <= 0) {
    decimalStr =
      sign + '0.' + valStr.padStart(valStr.length - decimalPointIdx, '0');
  } else {
    decimalStr =
      sign +
      valStr.slice(0, decimalPointIdx) +
      '.' +
      valStr.slice(decimalPointIdx);
  }
  return decimalStr;
}

// This function helps avoid BigInt negative exponent out of range error.
// It also deals with rounding
export function adjustDecimals(
  value: bigint,
  decimalsDelta: number | bigint,
  round: 'floor' | 'ceil' = 'floor',
) {
  decimalsDelta = BigInt(decimalsDelta);
  if (decimalsDelta === 0n) return value;
  if (decimalsDelta > 0n) return value * 10n ** decimalsDelta;

  const quotient = value / 10n ** -decimalsDelta;
  if (round === 'floor') {
    return quotient;
  }

  // ceil
  const remainder = value - quotient * 10n ** -decimalsDelta;
  if (remainder === 0n) {
    return quotient;
  } else {
    return quotient + 1n;
  }
}

// export function bigIntToInt(value: bigint, decimals: number | bigint, round: 'floor' | 'ceil' | 'round' = 'floor') {
//   const decimalStr = bigIntToDecimalStr(value, decimals);
//   return Number(decimalStr);
// }

// FORMAT FOR DISPLAYING

// THIS FUNCTION IS FOR DISPLAYING VALUES NOT FOR CALCULATIONS!
export function bigIntToNum(value: bigint, decimals: number | bigint): number {
  const decimalStr = bigIntToDecimalStr(value, decimals);
  return Number(decimalStr);
}

const removeInsignificantZeros = (value: string, minDecimals: number = 0) => {
  // don't format numbers without decimal point
  if (!value.includes('.')) return value;
  for (let i = value.length - 1; i >= minDecimals + 1; i -= 1) {
    if (value.charAt(i) === '0' && value.charAt(i - minDecimals) !== '.') {
      // trim insignificant zeros from the end of decimal string leaving min decimals in place
      value = value.substring(0, i) + value.substring(i + 1);
    } else {
      break;
    }
  }
  return value;
};

type ValueFormatOptions = {
  style?: 'currency' | 'percent' | 'decimal' | 'leverage' | 'slippage' | null;
  digits?: number | null; // enforces exactly digits
  showChange?: boolean;
  compact?: boolean;
  // showCurrency?: boolean;
  round?: 'floor' | 'ceil' | 'expand' | 'trunc';
  abs?: boolean;
  commas?: boolean;
  maxDigits?: number | null;
};

/**
 * Format a decimal or integer value. Includes comma separators.
 *
 * @param {*} value The value to be formatted
 * @param {*} style 'currency', 'percent', or null (or 'decimal') for default value formatting
 * @param {*} digits The number of fractional digits the number should have. If null (default), then uses the original number
 * @param {*} showChange Whether to include `+` before positive numbers. default: false
 * @param {*} compact Whether to compact the number in scientific form. default: false
 * @param {*} round Whether to round the number. default: 'floor'
 * @returns A string representing the value
 */
export function formatNumber(
  rawValue: number | string | undefined,
  options: ValueFormatOptions = {},
) {
  if (rawValue === undefined) return '-';

  let value = rawValue;
  if (options.style === 'slippage') {
    value = Math.max(0, Number(rawValue));
  }

  let formattedValue = new Intl.NumberFormat('en-US', {
    // style: style === "percent" ? "percent" : "decimal",
    // ...(style === 'currency' && showCurrency
    //   ? { style: 'currency', currency: 'USD' })
    // : { style: style === 'percent' ? 'percent' : 'decimal' }),
    notation: options.compact ? 'compact' : 'standard',
    minimumFractionDigits: options.digits ?? 0,
    maximumFractionDigits: options.digits ?? options.maxDigits ?? 20,
    signDisplay: options.abs ? 'never' : options.showChange ? 'always' : 'auto',
    roundingMode: options.round ?? 'floor',
    useGrouping: options.commas ?? true,
  }).format(Number(value));

  // return style !== 'percent'
  //   ? removeInsignificantZeros(
  //       formattedValue,
  //       (digits || 0) > 2 ? 4 : digits || 0,
  //     )
  //   : formattedValue;

  let suffix = '';
  switch (options.style) {
    case 'slippage':
    case 'percent':
      suffix = '%';
      break;
    case 'leverage':
      suffix = 'X';
      break;
  }

  switch (options.style) {
    case 'currency':
      if (formattedValue.startsWith('+') || formattedValue.startsWith('-')) {
        formattedValue =
          formattedValue.charAt(0) + '$' + formattedValue.slice(1);
      } else {
        formattedValue = '$' + formattedValue;
      }
      break;
  }

  return removeInsignificantZeros(formattedValue, options.digits ?? 0) + suffix;
}

export function formatBigInt(
  value: bigint | undefined,
  decimals: number | bigint,
  options: ValueFormatOptions = {},
) {
  return formatNumber(
    value === undefined ? undefined : bigIntToNum(value, decimals),
    options,
  );
}

export const validatedDecimalStr = (
  value: string,
  maxDecimals: number,
  maxLength = Infinity,
  allowSign = false,
) => {
  // Preserve sign if allowed
  const sign =
    allowSign && (value.startsWith('+') || value.startsWith('-'))
      ? value[0]
      : '';

  const dotAdjustedVal = value.startsWith('.') ? `0${value}` : value;

  // Remove sign from value for processing
  let validatedVal = dotAdjustedVal
    .replace(/^[+-]/, '')
    .replace(maxDecimals > 0 ? /[^0-9.]/g : /[^0-9]/g, '');

  // truncate decimals
  const match = validatedVal.match(
    new RegExp(`^(\\d+(\\.\\d{0,${maxDecimals}})?)?`),
  );
  if (match) {
    validatedVal = match[0];
  }

  if (validatedVal.length > maxLength) {
    validatedVal = validatedVal.slice(0, maxLength);
  }

  return sign + validatedVal;
};

type formatDateOptions = {
  showSeconds?: boolean;
  showTime?: boolean;
  showYear?: boolean;
  showDate?: boolean;
  textMonth?: boolean;
  swapDateTime?: boolean;
};

export const formatDate = (
  date: Date | number | undefined,
  {
    showSeconds,
    showTime,
    showYear = true,
    showDate = true,
    textMonth = false,
    swapDateTime = false,
  }: formatDateOptions = {},
) => {
  try {
    if (!date) return '-';
    if (date instanceof Date && Number.isNaN(date.getTime())) {
      return '-';
    }

    let result = '';

    if (showDate) {
      result = new Intl.DateTimeFormat('en-US', {
        month: textMonth ? 'short' : 'numeric',
        day: 'numeric',
        ...(showYear && { year: 'numeric' }),
      }).format(date);
    }

    if (showTime) {
      const timeStr = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: showSeconds ? '2-digit' : undefined,
        hour12: false,
      }).format(date);

      if (result) {
        result = swapDateTime ? `${timeStr} ${result}` : `${result} ${timeStr}`;
      } else {
        result = timeStr;
      }
    }

    return result || '-';
  } catch (e) {
    console.error(`Error formatting date ${date}:`, e);
    return '-';
  }
};

export const formatOrderType = (
  type: OrderType,
  t?: (key: string) => string,
) => {
  if (!type) {
    return '-';
  }

  if (t) {
    return t(`orderType.${type}`);
  }

  switch (type) {
    case OrderType.MARKET:
      return 'Market';
    case OrderType.LIMIT:
      return 'Limit';
    case OrderType.STOP_LOSS:
      return 'Stop Loss';
    case OrderType.TAKE_PROFIT:
      return 'Take Profit';
    case OrderType.LIQUIDATION:
      return 'Liquidation';
    default:
      return '-';
  }
};

export const formatLpOrderType = (
  type: LPOrderType,
  t?: (key: string) => string,
) => {
  if (t) {
    return t(`lpOrderType.${type}`);
  }
  switch (type) {
    case LPOrderType.DEPOSIT:
      return 'Deposit';
    case LPOrderType.SCHEDULE_WITHDRAW:
      return 'Scheduled Withdraw';
    case LPOrderType.IMMEDIATE_WITHDRAW:
      return 'Immediate Withdraw';
  }
};

export const formatOrderStatus = (
  status: OrderStatus,
  t?: (key: string) => string,
  isTransfer?: boolean,
) => {
  if (t) {
    if (isTransfer && status === OrderStatus.NEW)
      return t(`transferStatus.${status}`);
    return t(`orderStatus.${status}`);
  }
  return status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatTransferType = (type: TransferType) => {
  switch (type) {
    case TransferType.DEPOSIT:
      return config.isDevelopment ? 'Funding' : 'Deposit';
    case TransferType.FUNDED_DEPOSIT:
      return 'Funding';
    case TransferType.CREDIT:
      return 'Credit';
    case TransferType.WITHDRAW:
    case TransferType.FUNDED_PROFIT_WITHDRAW:
      return 'Withdraw';
    default:
      return '-';
  }
};

export const formatOrderHistoryFilterType = (
  type: OrderHistoryFilterType,
  t?: (key: string) => string,
) => {
  if (t) {
    return t(`orderHistoryFilterType.${type}`);
  }
  switch (type) {
    case OrderHistoryFilterType.ALL:
      return 'All';
    case OrderHistoryFilterType.FILLED:
      return 'Filled';
    case OrderHistoryFilterType.REJECTED:
      return 'Rejected';
    case OrderHistoryFilterType.CANCELLED:
      return 'Cancelled';
    default:
      return '-';
  }
};

export const formatTableTabType = (
  type: TableTabType,
  t?: (key: string) => string,
) => {
  if (t) {
    return t(`tableTabType.${type}`);
  }
  switch (type) {
    case TableTabType.POSITIONS:
      return 'Positions';
    case TableTabType.OPEN_ORDERS:
      return 'Open Orders';
    case TableTabType.ORDER_HISTORY:
      return 'Order History';
    case TableTabType.BALANCES:
      return 'Balances';
    case TableTabType.TRANSFERS:
      return 'Transfers';
    case TableTabType.LOCKED_ASSET_COLLATERAL:
      return 'Locked Asset Collateral';
    default:
      return '-';
  }
};

export const formatAssetType = (
  type: AssetType,
  t?: (key: string) => string,
) => {
  if (t) {
    return t(`assetType.${type}`);
  }
  switch (type) {
    case AssetType.STOCK:
      return 'Stock';
    case AssetType.FOREX:
      return 'Forex';
    case AssetType.CRYPTO:
      return 'Crypto';
    default:
      return '-';
  }
};

export function centsToDollars(cents: number | string): number {
  return Number(cents) / 100;
}
